import { useState } from "react"
import { ButtonSecondary, ButtonTertiary } from "@nova/component-btn"
import { FlightUploadDropbox } from "./components/FlightUploadDropbox"
import { useWorkspacePermissions } from "features/data-management/workspace/useWorkspacePermissions"
import { useUsageExceeded } from "features/data-management/workspace/subscriptions/hooks/useUsageExceeded"
import { useUploadFlowContext } from "panels/content/side-panel/upload-flow/useUploadFlowContext"
import { WorkspaceTextInput } from "@nova/component-inline-text-input"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import { useEffectUnsafe } from "@nova/use-effect-unsafe"
import { GenericDataUsage } from "./components/GenericDataUsage"
import { NovaSpinner } from "@nova/component-nova-spinner"
import { useLoadingText } from "features/data-management/workspace/workspace/useLoadingText"
import { setActiveFeatureId, setActiveLayerId } from "../slice.new"
import { selectIsDomainWhiteListed, selectUserLicense } from "user/profile/selectors"
import * as Icons from "@nova/component-icons"
import { Licenses } from "@nova/constants"
import { LoadFromLayerButton, loadFromLayerTargetType } from "./LoadFromLayer"

export const GenericAddFiles = () => {
    const dispatch = useAppDispatch()
    const { isEditor } = useWorkspacePermissions()
    const {
        portfolioId,
        fireId,
        flightId,
        selectFlightById,
        updateFlight,
        deleteFlight,
        isDemo,
        sampleDataFlightKey,
        pluginId,
    } = useUploadFlowContext()
    const { isExceeded } = useUsageExceeded()
    const isWhitelisted = useAppSelector(selectIsDomainWhiteListed)
    const currentLicense = useAppSelector(selectUserLicense)
    const currentLicenseType = currentLicense?.subscriptionType || Licenses.Lite

    const demoDataLoadingText = useLoadingText(isDemo, "Loading sample data")

    const flight = useAppSelector(selectFlightById(flightId))

    const [nameInput, setNameInput] = useState(flight.name)

    useEffectUnsafe(() => {
        if (!nameInput || nameInput === flight.name) return
        dispatch(updateFlight({ id: flightId, updates: { name: nameInput } }))
    }, [nameInput])

    if (!portfolioId || !fireId || !flightId)
        throw new Error("Data tab is open without all activated data")

    const isPaywalled = isExceeded
    const canLoadFromLayer =
        isEditor && !isPaywalled && !isDemo && !!loadFromLayerTargetType(pluginId)

    const shouldShowSampleDataButton =
        !!sampleDataFlightKey && !isDemo && currentLicenseType === Licenses.Lite && !isWhitelisted

    return (
        <div className="-mt-px">
            {!isDemo && (
                <>
                    <small className="text-gray-100">Layer Name</small>
                    <div className="mt-2" />
                    <WorkspaceTextInput
                        name={"layer-name"}
                        value={nameInput}
                        handleChange={setNameInput}
                        inputClassName="!text-gray-0"
                    />
                </>
            )}
            {isEditor && !isPaywalled && !isDemo && (
                <div className="mt-4">
                    <FlightUploadDropbox />
                </div>
            )}
            {canLoadFromLayer && (
                <>
                    <div className="flex items-center gap-2 my-3">
                        <div className="flex-1 border-b border-gray-700" />
                        <small className="text-gray-500">or</small>
                        <div className="flex-1 border-b border-gray-700" />
                    </div>
                    <LoadFromLayerButton className="mb-4" />
                </>
            )}
            {isDemo && (
                <div className="flex h-5 gap-2 items-center m-3">
                    <NovaSpinner width={20} height={20} />
                    <h5 className="text-gray-0 select-none">{demoDataLoadingText}</h5>
                </div>
            )}
            {isPaywalled && <GenericDataUsage />}
            {shouldShowSampleDataButton && (
                <ButtonSecondary
                    title="Use Sample Data"
                    RightIcon={Icons.ArrowRight}
                    className="mt-2 w-full"
                    onClick={() =>
                        dispatch(
                            updateFlight({
                                id: flightId,
                                updates: {},
                                context: { sampleDataFlightKey },
                            })
                        )
                    }
                />
            )}
            <ButtonTertiary
                title="Cancel"
                className="w-full mt-2"
                onClick={() => {
                    dispatch(setActiveLayerId({ id: undefined }))
                    dispatch(setActiveFeatureId({ feature: undefined }))
                    dispatch(deleteFlight({ id: flightId }))
                }}
            />
        </div>
    )
}
